import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { questionService, type Question } from "@/services/questionService";
import { extractQuestionFromBody } from "@/utils/questionValidation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle, Search, Filter, AlertTriangle, ArrowLeft, RefreshCw, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function QuestionAudit() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [repairResult, setRepairResult] = useState<{ repairedCount: number; unrecoverableCount: number; errors: string[] } | null>(null);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const list = await questionService.getQuestions();
      setQuestions(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRepair = async () => {
    if (!confirm("Isso irá extrair automaticamente as perguntas do corpo do texto e salvá-las no banco. Deseja continuar?")) return;
    
    setRepairing(true);
    setRepairResult(null);
    try {
      const res = await questionService.repairLegacyQuestions();
      setRepairResult(res);
      loadQuestions();
    } catch (err: any) {
      alert(err.message || "Erro durante a reparação.");
    } finally {
      setRepairing(false);
    }
  };

  const auditData = questions.map(q => {
    const hasExplicitQuestion = !!(
      q.question?.trim() ||
      q.questionText?.trim() ||
      q.specificQuestion?.trim() ||
      q.prompt?.trim() ||
      q.statement?.trim() ||
      q.metadata?.question?.trim() ||
      q.metadata?.prompt?.trim()
    );

    const bodyText = q.text || q.body || q.content || q.context || "";
    const extraction = extractQuestionFromBody(bodyText);
    
    const isRecoverable = !hasExplicitQuestion && !!extraction.extractedQuestion;
    const isUnrecoverable = !hasExplicitQuestion && !extraction.extractedQuestion;

    return {
      ...q,
      hasExplicitQuestion,
      isRecoverable,
      isUnrecoverable,
      extractedQuestion: extraction.extractedQuestion,
      cleanBody: extraction.cleanBody,
      bodyText
    };
  });

  const stats = {
    total: auditData.length,
    hasQuestion: auditData.filter(q => q.hasExplicitQuestion).length,
    recoverable: auditData.filter(q => q.isRecoverable).length,
    unrecoverable: auditData.filter(q => q.isUnrecoverable).length
  };

  const filteredUnrecoverable = auditData
    .filter(q => q.isUnrecoverable)
    .filter(q => 
      q.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      q.section.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.bodyText.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <div className="container mx-auto p-6 space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/questions")}
            className="text-gray-500 hover:text-gray-700 -ml-2 mb-2 rounded-lg gap-1.5 font-semibold text-xs"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Banco de Questões
          </Button>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Auditoria de Qualidade</h1>
          <p className="text-gray-500 mt-1">Diagnóstico e reparo automático de questões legadas.</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={loadQuestions} 
            variant="outline" 
            size="sm" 
            disabled={loading || repairing}
            className="rounded-xl gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button 
            onClick={handleRepair} 
            disabled={loading || repairing || stats.recoverable === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-2 shadow-md"
          >
            <Wrench className={`w-4 h-4 ${repairing ? 'animate-spin' : ''}`} />
            {repairing ? "Reparando..." : "Reparar Automáticos"}
          </Button>
        </div>
      </div>

      {repairResult && (
        <Card className={`border-2 ${repairResult.errors.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              {repairResult.errors.length > 0 ? <AlertTriangle className="w-6 h-6 text-amber-600" /> : <CheckCircle className="w-6 h-6 text-green-600" />}
              <div className="space-y-1">
                <h3 className="font-bold text-gray-900">Reparação Concluída</h3>
                <p className="text-sm text-gray-700">
                  Questões reparadas: <strong>{repairResult.repairedCount}</strong>. 
                  Questões pendentes: <strong>{repairResult.unrecoverableCount}</strong>.
                </p>
                {repairResult.errors.length > 0 && (
                  <ul className="text-xs text-red-600 mt-2 list-disc pl-4">
                    {repairResult.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                    {repairResult.errors.length > 5 && <li>E mais {repairResult.errors.length - 5} erros...</li>}
                  </ul>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-sm border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold text-gray-400 uppercase">Total Geral</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-green-100 bg-green-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold text-green-600 uppercase">Perguntas OK</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-green-700">{stats.hasQuestion}</div>
            <p className="text-[10px] text-green-600 mt-1">Possuem campo prompt preenchido.</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-blue-100 bg-blue-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold text-blue-600 uppercase">Recuperáveis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-blue-700">{stats.recoverable}</div>
            <p className="text-[10px] text-blue-600 mt-1">Perguntas detectadas no final do body.</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-red-200 bg-red-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold text-red-600 uppercase">Críticos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-red-700">{stats.unrecoverable}</div>
            <p className="text-[10px] text-red-600 mt-1">Sem prompt e sem pergunta no body.</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm border-gray-100">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Diagnóstico de Pendências
            </CardTitle>
            <CardDescription>Questões que precisam de atenção manual ou reparo automático.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              placeholder="Filtrar por título, seção ou conteúdo..." 
              className="pl-9 rounded-xl bg-gray-50 border-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="border rounded-2xl overflow-hidden shadow-sm">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="font-bold">Status</TableHead>
                  <TableHead className="font-bold">Título / Seção</TableHead>
                  <TableHead className="font-bold w-1/2">Corpo do Texto</TableHead>
                  <TableHead className="font-bold text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditData
                  .filter(q => q.isRecoverable || q.isUnrecoverable)
                  .filter(q => 
                    q.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    q.section.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    q.bodyText.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map(q => (
                    <TableRow key={q.id} className="hover:bg-gray-50/50 transition-colors">
                      <TableCell>
                        {q.isRecoverable ? (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none text-[10px]">REPARÁVEL</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-none text-[10px]">CRÍTICO</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-bold text-gray-900">{q.title}</div>
                        <div className="text-[10px] text-gray-500">{q.section}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-600 line-clamp-2 italic">
                          {q.bodyText || "(Vazio)"}
                        </div>
                        {q.extractedQuestion && (
                          <div className="mt-1 text-[10px] text-blue-600 font-bold bg-blue-50 p-1 rounded inline-block">
                            Detectada: "{q.extractedQuestion}"
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm" className="rounded-lg text-primary hover:bg-primary/10">
                          <Link to={`/questions/${q.id}/edit`}>Editar</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {stats.recoverable === 0 && stats.unrecoverable === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <CheckCircle className="w-8 h-8 text-green-500" />
                        <p className="font-bold text-gray-700">Tudo em ordem!</p>
                        <p className="text-xs text-gray-500">Todas as questões possuem enunciado próprio.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
